import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
    FileSearch,
    Upload,
    Loader2,
    Check,
    AlertTriangle,
    X,
    Sparkles,
    Trash2,
    FileSignature,
} from "lucide-react";
import { toast } from "sonner";
import { ProrogaLetterDialog, LETTER_LABELS } from "../components/ProrogaLetterDialog";

const fmtDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = String(iso).split("-");
    return y && m && d ? `${d}/${m}/${y}` : iso;
};

const STATUS_BADGE = {
    queued: { label: "In attesa", cls: "bg-slate-100 text-slate-600" },
    processing: { label: "Analisi AI…", cls: "bg-blue-100 text-blue-800" },
    archived: { label: "Archiviata", cls: "bg-emerald-100 text-emerald-800" },
    unmatched: { label: "Non abbinata", cls: "bg-amber-100 text-amber-800" },
    error: { label: "Errore", cls: "bg-red-100 text-red-700" },
};

export default function UnilavBulkPage() {
    const [items, setItems] = useState([]); // {id, file, status, extracted, employee, reminderId, error, manualEmployeeId, archiving}
    const [running, setRunning] = useState(false);
    const [prorogaTarget, setProrogaTarget] = useState(null); // { employeeId, recordId, itemId }
    const [employees, setEmployees] = useState([]);
    const inputRef = useRef(null);

    useEffect(() => {
        api.get("/employees")
            .then(({ data }) => setEmployees(data))
            .catch(() => {});
    }, []);

    const addFiles = (fileList) => {
        const pdfs = Array.from(fileList).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
        if (pdfs.length !== fileList.length) toast.warning("Solo file PDF: gli altri sono stati ignorati");
        setItems((prev) => [
            ...prev,
            ...pdfs.map((f) => ({
                id: `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                file: f,
                status: "queued",
                extracted: null,
                employee: null,
                reminderId: null,
                error: null,
                manualEmployeeId: "",
                archiving: false,
            })),
        ]);
    };

    const updateItem = (id, patch) =>
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

    const archiveFor = async (item, employeeId, reminderId) => {
        let recordId = null;
        const fd = new FormData();
        fd.append("file", item.file);
        fd.append("employee_id", employeeId);
        fd.append("category", "unilav");
        const ex = item.extracted || {};
        const tipo = ex.tipo_comunicazione ? ` ${ex.tipo_comunicazione}` : "";
        fd.append("title", `Ricevuta UNILAV${tipo}${ex.data_invio ? ` del ${fmtDate(ex.data_invio)}` : ""}`);
        await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        try {
            const { data: rec } = await api.post("/unilav/records", { employee_id: employeeId, extracted: ex, source: "bulk" });
            recordId = rec?.id || null;
            if (rec?.employee_update) {
                const eu = rec.employee_update;
                toast.info(eu.contratto_scadenza === null
                    ? "Scheda aggiornata: contratto trasformato a INDETERMINATO (scadenza rimossa)"
                    : `Scheda aggiornata: contratto prorogato al ${(eu.contratto_scadenza || "").split("-").reverse().join("/")}`);
            }
        } catch (e) { console.warn("Aggiornamento scheda non riuscito (non bloccante):", e?.message); }
        if (reminderId) {
            try {
                await api.put(`/unilav/reminders/${reminderId}`, { status: "done" });
            } catch (e) { console.warn("Chiusura promemoria UNILAV fallita (non bloccante):", e?.message); }
        }
        return recordId;
    };

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    // Network drop / proxy error (Cloudflare 5xx HTML, no JSON detail) / timeout → worth retrying
    const isTransient = (e) =>
        !e.response || e.code === "ECONNABORTED" || (e.response.status >= 500 && !e.response.data?.detail);

    const processOne = async (item) => {
        let job = null;
        for (let attempt = 1; attempt <= 3 && !job; attempt++) {
            try {
                const fd = new FormData();
                fd.append("file", item.file);
                const { data } = await api.post("/unilav/process-async", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 60000,
                });
                job = data;
            } catch (e) {
                if (!isTransient(e) || attempt === 3) throw e;
                updateItem(item.id, { error: `Problema di rete, nuovo tentativo ${attempt + 1}/3…` });
                await sleep(3000 * attempt);
            }
        }
        const started = Date.now();
        let pollFailures = 0;
        while (Date.now() - started < 240000) {
            await sleep(2500);
            try {
                const { data } = await api.get(`/unilav/process-jobs/${job.job_id}`);
                pollFailures = 0;
                if (data.status === "done") return data.result;
                if (data.status === "error") throw Object.assign(new Error(data.error), { detail: data.error });
            } catch (e) {
                if (e.detail) throw e;
                if (!isTransient(e) || ++pollFailures >= 6) throw e;
            }
        }
        throw Object.assign(new Error("timeout"), { detail: "Tempo massimo superato: riprova" });
    };

    const processAll = async () => {
        setRunning(true);
        const queue = items.filter((it) => it.status === "queued" || it.status === "error");
        for (const item of queue) {
            updateItem(item.id, { status: "processing", error: null });
            try {
                const data = await processOne(item);
                if (data.employee) {
                    const recId = await archiveFor({ ...item, extracted: data.extracted }, data.employee.id, data.reminder_id);
                    updateItem(item.id, {
                        status: "archived",
                        extracted: data.extracted,
                        employee: data.employee,
                        reminderId: data.reminder_id,
                        employeeCreated: !!data.employee_created,
                        matchNote: data.match_note || null,
                        unilavRecordId: recId,
                    });
                    if (data.employee_created) {
                        setEmployees((prev) => [...prev, data.employee]);
                    }
                } else {
                    updateItem(item.id, {
                        status: "unmatched",
                        extracted: data.extracted,
                        suggested: data.suggested_employee || null,
                        manualEmployeeId: data.suggested_employee?.id || "",
                    });
                }
            } catch (e) {
                updateItem(item.id, {
                    status: "error",
                    error: e.response?.data?.detail || e.detail
                        || (isTransient(e) ? "Errore temporaneo del server: rilancia l'elaborazione" : "Errore di elaborazione"),
                });
            }
        }
        setRunning(false);
        toast.success("Elaborazione completata");
    };

    const archiveManual = async (item) => {
        if (!item.manualEmployeeId) return;
        updateItem(item.id, { archiving: true });
        try {
            // Check for a matching pending reminder for the chosen employee
            let reminderId = null;
            const evt = (item.extracted?.tipo_comunicazione || "").toLowerCase();
            if (evt) {
                try {
                    const { data: rems } = await api.get("/unilav/reminders", { params: { status: "pending" } });
                    reminderId = rems.find((r) => r.employee_id === item.manualEmployeeId && r.event_type === evt)?.id || null;
                } catch (e) { console.warn("Ricerca promemoria UNILAV fallita:", e?.message); }
            }
            const recId = await archiveFor(item, item.manualEmployeeId, reminderId);
            const emp = employees.find((e) => e.id === item.manualEmployeeId);
            updateItem(item.id, {
                status: "archived",
                employee: emp ? { id: emp.id, cognome: emp.cognome, nome: emp.nome, matricola: emp.matricola } : null,
                archiving: false,
                unilavRecordId: recId,
            });
            toast.success("Ricevuta archiviata");
        } catch (e) {
            updateItem(item.id, { archiving: false });
            toast.error(e.response?.data?.detail || "Errore nell'archiviazione");
        }
    };

    const createManual = async (item) => {
        updateItem(item.id, { archiving: true });
        try {
            const { data } = await api.post("/unilav/create-employee", { extracted: item.extracted || {} });
            const recId = await archiveFor(item, data.employee.id, data.reminder_id);
            setEmployees((prev) => [...prev, data.employee]);
            updateItem(item.id, { status: "archived", employee: data.employee, employeeCreated: true, archiving: false, unilavRecordId: recId });
            toast.success(`Dipendente creato (matr. ${data.employee.matricola}) e ricevuta archiviata`);
        } catch (e) {
            updateItem(item.id, { archiving: false });
            toast.error(e.response?.data?.detail || "Errore nella creazione del dipendente");
        }
    };

    const queuedCount = items.filter((i) => i.status === "queued" || i.status === "error").length;
    const stats = {
        archived: items.filter((i) => i.status === "archived").length,
        unmatched: items.filter((i) => i.status === "unmatched").length,
        error: items.filter((i) => i.status === "error").length,
    };

    return (
        <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8" data-testid="unilav-bulk-page">
            <div className="mb-6">
                <h1 className="flex items-center gap-2 font-display text-3xl font-bold text-[#376DB3]">
                    <FileSearch className="h-7 w-7" />
                    UNILAV — Caricamento massivo
                </h1>
                <p className="mt-1 text-sm text-slate-600">
                    Trascina o seleziona più ricevute UNILAV in PDF: l'AI le analizza e le archivia automaticamente nel
                    fascicolo del dipendente giusto tramite il codice fiscale. Se la ricevuta è un'<b>assunzione</b> e il
                    codice fiscale non esiste in anagrafica, il <b>dipendente viene creato automaticamente</b> (matricola
                    assegnata in automatico). I promemoria corrispondenti vengono segnati come inviati.
                </p>
            </div>

            {/* Dropzone */}
            <input
                type="file"
                accept="application/pdf"
                multiple
                ref={inputRef}
                className="hidden"
                onChange={(e) => {
                    if (e.target.files?.length) addFiles(e.target.files);
                    e.target.value = "";
                }}
                data-testid="bulk-file-input"
            />
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
                }}
                className="mb-4 flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-sm text-slate-500 transition hover:border-[#376DB3]/50 hover:bg-[#376DB3]/5"
                data-testid="bulk-dropzone"
            >
                <Upload className="h-7 w-7 text-slate-400" />
                <span>Trascina qui le ricevute UNILAV (PDF) oppure clicca per selezionarle</span>
            </button>

            {/* Actions + stats */}
            <div className={`mb-4 flex flex-wrap items-center justify-between gap-3 ${items.length > 0 ? "" : "hidden"}`}>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                    <Badge variant="outline">{items.length} file</Badge>
                    {stats.archived > 0 && <Badge className="bg-emerald-100 text-emerald-800">{stats.archived} archiviate</Badge>}
                    {stats.unmatched > 0 && <Badge className="bg-amber-100 text-amber-800">{stats.unmatched} non abbinate</Badge>}
                    {stats.error > 0 && <Badge className="bg-red-100 text-red-700">{stats.error} errori</Badge>}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setItems((prev) => prev.filter((i) => i.status === "processing"))}
                        disabled={running}
                        className="gap-1.5"
                        data-testid="bulk-clear-btn"
                    >
                        <Trash2 className="h-3.5 w-3.5" /> Svuota lista
                    </Button>
                    <Button
                        onClick={processAll}
                        disabled={running || queuedCount === 0}
                        className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="bulk-process-btn"
                    >
                        {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                        {running ? "Elaborazione…" : `Analizza e archivia (${queuedCount})`}
                    </Button>
                </div>
            </div>

            {/* Rows */}
            <div className="space-y-2">
                {items.map((item) => {
                    const badge = STATUS_BADGE[item.status];
                    const ex = item.extracted || {};
                    return (
                        <div
                            key={item.id}
                            className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
                            data-testid={`bulk-row-${item.id}`}
                        >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
                                    <Badge className={badge.cls}>
                                        {item.status === "processing" && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                                        {badge.label}
                                    </Badge>
                                    <span className="truncate font-medium text-slate-700">{item.file.name}</span>
                                    {ex.tipo_comunicazione && (
                                        <span className="text-xs capitalize text-slate-500">{ex.tipo_comunicazione}</span>
                                    )}
                                    {ex.lavoratore_codice_fiscale && (
                                        <span className="font-mono text-xs text-slate-400">{ex.lavoratore_codice_fiscale}</span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    {item.status === "archived" && item.employee && (
                                        <>
                                            {item.employeeCreated && (
                                                <Badge className="bg-[#5BAEDB]/20 text-[#15498F]" data-testid={`bulk-created-badge-${item.id}`}>
                                                    <Sparkles className="mr-1 h-3 w-3" />
                                                    Nuovo dipendente creato
                                                </Badge>
                                            )}
                                            <Link
                                                to={`/dipendenti/${item.employee.id}`}
                                                className="flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline"
                                                data-testid={`bulk-archived-link-${item.id}`}
                                            >
                                                <Check className="h-3.5 w-3.5" />
                                                {item.employee.cognome} {item.employee.nome} (matr. {item.employee.matricola})
                                            </Link>
                                            {LETTER_LABELS[(ex.tipo_comunicazione || "").toLowerCase()] && (
                                                <Button
                                                    size="sm"
                                                    variant={item.prorogaLetterDone ? "outline" : "default"}
                                                    onClick={() => setProrogaTarget({ employeeId: item.employee.id, recordId: item.unilavRecordId, itemId: item.id, kind: (ex.tipo_comunicazione || "").toLowerCase() })}
                                                    className={`h-7 gap-1.5 text-xs ${item.prorogaLetterDone ? "" : "bg-[#376DB3] text-white hover:bg-[#376DB3]/90"}`}
                                                    data-testid={`bulk-proroga-letter-${item.id}`}
                                                >
                                                    <FileSignature className="h-3 w-3" />
                                                    {item.prorogaLetterDone ? LETTER_LABELS[(ex.tipo_comunicazione || "").toLowerCase()].done : LETTER_LABELS[(ex.tipo_comunicazione || "").toLowerCase()].button}
                                                </Button>
                                            )}
                                        </>
                                    )}
                                    {item.status === "processing" && item.error && (
                                        <span className="text-xs text-amber-700" data-testid={`bulk-retry-${item.id}`}>{item.error}</span>
                                    )}
                                    {item.status === "error" && (
                                        <span className="flex items-center gap-1 text-xs text-red-600">
                                            <AlertTriangle className="h-3.5 w-3.5" /> {item.error}
                                        </span>
                                    )}
                                    {(item.status === "queued" || item.status === "error") && !running && (
                                        <button
                                            type="button"
                                            onClick={() => setItems((prev) => prev.filter((i) => i.id !== item.id))}
                                            className="flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                                            title="Rimuovi dalla lista"
                                            data-testid={`bulk-remove-${item.id}`}
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            {item.status === "archived" && item.matchNote && (
                                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-1.5 text-xs text-amber-800" data-testid={`bulk-match-note-${item.id}`}>
                                    <AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> {item.matchNote}
                                </div>
                            )}
                            {item.status === "unmatched" && (
                                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2">
                                    <span className="text-xs text-amber-800">
                                        Nessun dipendente con il codice fiscale letto dall'AI
                                        {ex.lavoratore_codice_fiscale ? ` «${ex.lavoratore_codice_fiscale}»` : ""}
                                        {ex.lavoratore_cognome ? ` (${ex.lavoratore_cognome} ${ex.lavoratore_nome || ""})` : ""}.
                                        {item.suggested
                                            ? ` Possibile corrispondenza per ${item.suggested.reason === "nome" ? "nome e cognome" : "cognome"}: verifica il CF in anagrafica e conferma.`
                                            : (ex.tipo_comunicazione || "").toLowerCase() === "assunzione"
                                                ? " Creazione automatica non riuscita (dati incompleti nella ricevuta). Assegna manualmente:"
                                                : " Assegna manualmente:"}
                                    </span>
                                    <Select
                                        value={item.manualEmployeeId}
                                        onValueChange={(v) => updateItem(item.id, { manualEmployeeId: v })}
                                    >
                                        <SelectTrigger className="h-8 w-[260px] bg-white text-xs" data-testid={`bulk-manual-select-${item.id}`}>
                                            <SelectValue placeholder="Scegli dipendente" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {employees.map((e) => (
                                                <SelectItem key={e.id} value={e.id}>
                                                    {e.cognome} {e.nome} · {e.matricola}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button
                                        size="sm"
                                        onClick={() => archiveManual(item)}
                                        disabled={!item.manualEmployeeId || item.archiving}
                                        className="h-8 gap-1.5 bg-[#376DB3] text-xs text-white hover:bg-[#376DB3]/90"
                                        data-testid={`bulk-manual-archive-${item.id}`}
                                    >
                                        {item.archiving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                        Archivia
                                    </Button>
                                    {(ex.tipo_comunicazione || "").toLowerCase() === "assunzione" && ex.lavoratore_codice_fiscale && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => createManual(item)}
                                            disabled={item.archiving}
                                            className="h-8 gap-1.5 text-xs"
                                            data-testid={`bulk-create-employee-${item.id}`}
                                        >
                                            <Sparkles className="h-3 w-3" /> Crea nuovo dipendente
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <ProrogaLetterDialog
                open={!!prorogaTarget}
                onOpenChange={(v) => !v && setProrogaTarget(null)}
                employeeId={prorogaTarget?.employeeId}
                recordId={prorogaTarget?.recordId}
                kind={prorogaTarget?.kind || "proroga"}
                onDone={() => prorogaTarget && updateItem(prorogaTarget.itemId, { prorogaLetterDone: true })}
            />
        </div>
    );
}
